/**
 * Timezone utilities for consistent time handling across the app.
 * 
 * All workout times are stored and displayed in the user's preferred timezone.
 */

/**
 * Create a Date object for a specific hour on a specific date in the user's timezone.
 * 
 * @param date - The base date (year, month, day)
 * @param hour - The hour in the user's timezone (0-23)
 * @param userTimezone - The user's IANA timezone (e.g., 'America/Phoenix')
 * @returns Date object representing that time in the user's timezone
 */
export function createDateInTimezone(
  date: Date,
  hour: number,
  userTimezone?: string
): Date {
  // If no timezone provided, use local timezone
  if (!userTimezone) {
    const result = new Date(date);
    result.setHours(hour, 0, 0, 0);
    return result;
  }

  // Create a string representing the desired time in the user's timezone
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const hourStr = String(hour).padStart(2, '0');
  
  // Use Intl.DateTimeFormat to parse the time in the user's timezone
  // Format: "YYYY-MM-DD HH:00:00" in user's timezone
  const dateString = `${year}-${month}-${day}T${hourStr}:00:00`;
  
  // Create a formatter for the user's timezone
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });

  // Parse the components back to create a UTC timestamp
  // This is a workaround since JavaScript Date doesn't have built-in timezone support
  try {
    // Parse the date string as if it's in the user's timezone
    const parts = formatter.formatToParts(new Date(dateString + 'Z'));
    const components: Record<string, string> = {};
    parts.forEach(part => {
      if (part.type !== 'literal') {
        components[part.type] = part.value;
      }
    });

    // Create the date by specifying it as if it's in the user's local timezone
    // then adjusting to UTC
    const localDate = new Date(
      parseInt(components.year!),
      parseInt(components.month!) - 1,
      parseInt(components.day!),
      hour,
      0,
      0,
      0
    );

    return localDate;
  } catch (error) {
    console.warn('[timezone] Failed to create date in timezone, falling back to local:', error);
    const result = new Date(date);
    result.setHours(hour, 0, 0, 0);
    return result;
  }
}

/**
 * Format a date for display in the user's timezone.
 * 
 * @param date - The date to format
 * @param userTimezone - The user's IANA timezone
 * @param options - Intl.DateTimeFormat options
 * @returns Formatted date string
 */
export function formatInTimezone(
  date: Date,
  userTimezone?: string,
  options?: Intl.DateTimeFormatOptions
): string {
  const defaultOptions: Intl.DateTimeFormatOptions = {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  };

  if (!userTimezone) {
    return date.toLocaleString([], defaultOptions);
  }

  return date.toLocaleString([], {
    ...defaultOptions,
    timeZone: userTimezone,
  });
}

/**
 * Get the current hour in the user's timezone.
 * Useful for determining "now" in the user's local time.
 * 
 * @param userTimezone - The user's IANA timezone
 * @returns Current hour (0-23) in the user's timezone
 */
export function getCurrentHourInTimezone(userTimezone?: string): number {
  const now = new Date();
  
  if (!userTimezone) {
    return now.getHours();
  }

  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: userTimezone,
    hour: 'numeric',
    hour12: false,
  });

  const hour = formatter.format(now);
  return parseInt(hour, 10);
}
